import { dateLocale } from "@/lib/dateTime";
import React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Briefcase, Plus, ExternalLink, Calendar, User, TrendingUp } from "lucide-react";
import { formatINR } from "@/lib/utils";
import type { DealRecord } from "@/types";

interface DealsWidgetProps {
  deals: DealRecord[];
  onNewDeal?: () => void;
  canCreate?: boolean;
}

export function DealsWidget({ deals, onNewDeal, canCreate }: DealsWidgetProps) {
  const totalValue = deals.reduce((acc, d) => acc + (Number(d.amount) || 0), 0);
  const openDeals = deals.filter((d) => d.deal_status === "OPEN");
  const wonDeals = deals.filter((d) => d.deal_status === "WON");

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "WON":
        return <Badge className="bg-success/10 text-success border-success/30 text-[11px]">Won</Badge>;
      case "LOST":
        return <Badge className="bg-danger/10 text-danger border-danger/30 text-[11px]">Lost</Badge>;
      default:
        return <Badge variant="outline" className="text-primary border-primary/30 bg-primary/5 text-[11px]">Open</Badge>;
    }
  };

  return (
    <div className="bg-card border border-border/80 rounded-2xl shadow-xs overflow-hidden">
      {/* Top Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-border/80">
        <div className="flex items-center gap-3">
          <div className="size-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Briefcase className="size-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold tracking-tight text-foreground">
                Deals ({deals.length})
              </h3>
              {deals.length > 0 && (
                <span className="text-xs font-bold text-foreground font-mono bg-muted px-2 py-0.5 rounded-md">
                  {formatINR(totalValue)} Total
                </span>
              )}
            </div>
            {deals.length > 0 && (
              <p className="text-[11px] text-muted-foreground">
                {openDeals.length} active • {wonDeals.length} won
              </p>
            )}
          </div>
        </div>

        {canCreate && onNewDeal && (
          <Button
            type="button"
            size="sm"
            onClick={onNewDeal}
            className="gap-1.5 self-start sm:self-center h-8 text-xs font-medium"
          >
            <Plus className="size-3.5" />
            <span>New Deal</span>
          </Button>
        )}
      </div>

      {/* Deals List / Table */}
      {deals.length === 0 ? (
        <div className="p-8 text-center space-y-3">
          <div className="size-10 rounded-full bg-muted flex items-center justify-center mx-auto text-muted-foreground">
            <Briefcase className="size-5" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">No deals linked</p>
            <p className="text-xs text-muted-foreground">
              There are currently no deals associated with this record.
            </p>
          </div>
          {canCreate && onNewDeal && (
            <Button type="button" variant="outline" size="sm" onClick={onNewDeal} className="gap-1.5 text-xs">
              <Plus className="size-3.5" />
              <span>Create First Deal</span>
            </Button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border/60">
                <TableHead className="text-xs font-semibold">Deal Name</TableHead>
                <TableHead className="text-xs font-semibold">Stage</TableHead>
                <TableHead className="text-xs font-semibold">Amount</TableHead>
                <TableHead className="text-xs font-semibold">Status</TableHead>
                <TableHead className="text-xs font-semibold">Expected Close</TableHead>
                <TableHead className="text-xs font-semibold">Owner</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deals.map((deal) => {
                const stageColor = deal.stage?.color_code || "#3b82f6";
                return (
                  <TableRow key={deal.id} className="hover:bg-muted/40 transition-colors">
                    <TableCell className="font-medium text-xs py-3">
                      <Link
                        to={`/deals/${deal.id}`}
                        className="text-foreground hover:text-primary font-semibold hover:underline inline-flex items-center gap-1 group"
                      >
                        <span className="truncate max-w-[200px] sm:max-w-xs">{deal.name}</span>
                        <ExternalLink className="size-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </Link>
                    </TableCell>

                    <TableCell className="text-xs py-3">
                      {deal.stage ? (
                        <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium border bg-card"
                          style={{ borderColor: `${stageColor}40` }}>
                          <span className="size-1.5 rounded-full" style={{ backgroundColor: stageColor }} />
                          <span className="text-foreground">{deal.stage.name}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>

                    <TableCell className="text-xs py-3 font-semibold font-mono text-foreground">
                      {formatINR(deal.amount)}
                    </TableCell>

                    <TableCell className="text-xs py-3">
                      {getStatusBadge(deal.deal_status)}
                    </TableCell>

                    <TableCell className="text-xs text-muted-foreground py-3">
                      {deal.expected_close_date ? (
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="size-3 text-muted-foreground/80" />
                          {new Date(deal.expected_close_date).toLocaleDateString(dateLocale(), {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </span>
                      ) : (
                        "—"
                      )}
                    </TableCell>

                    <TableCell className="text-xs text-muted-foreground py-3">
                      {deal.owner ? (
                        <span className="inline-flex items-center gap-1 text-foreground/80">
                          <User className="size-3 text-muted-foreground" />
                          {deal.owner.first_name} {deal.owner.last_name || ""}
                        </span>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
